import { isEmpty } from '@ember/utils'
import fillMonths from 'district-ui-client/utils/student-events/fill-months'
import type { NormalizedInterval } from 'date-fns'
import type StudentEventsService from 'district-ui-client/services/student-events'
import type { StatsScope } from 'district-ui-client/utils/student-events/stats-scope'

export interface DrivingTestDetailsTotalsByMonthResponse {
  driving_tests_totals_summary_by_grade: SummaryData[]
  driving_tests_totals_by_grade_by_month: MonthData[]
}

interface SummaryData {
  grade_position: number
  data: [{ category: string; count: number }, { category: string; count: number }]
}

interface MonthData {
  grade_position: number
  months: {
    month: string
    count: number
  }[]
}

export interface DrivingTestsTotalsByGradeByMonthData {
  grade_position: number
  summary: {
    total_count: number
    student_count: number
  }
  months: {
    month: string
    count: number
  }[]
}

export async function drivingTestsTotalsByGradeByMonth(
  this: StudentEventsService,
  product: LegacyProductMs | LegacyProductRe,
  statsScope: StatsScope,
  period: NormalizedInterval,
): Promise<Nullable<DrivingTestsTotalsByGradeByMonthData[]>> {
  if (isEmpty(statsScope.ids)) return null
  const url = this.buildUrl('driving_tests_totals_by_grade_by_month', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<DrivingTestDetailsTotalsByMonthResponse>(url, query)
  if (!data) return null

  const { driving_tests_totals_summary_by_grade: summaryData, driving_tests_totals_by_grade_by_month: monthData } = data
  if (summaryData.length === 0) {
    return null
  }

  return groupByGrade(summaryData, monthData, period)
}

function groupByGrade(summaryData: SummaryData[], monthData: MonthData[], period: NormalizedInterval) {
  const sortedSummaryData = [...summaryData].sort((a, b) => a.grade_position - b.grade_position)

  return sortedSummaryData
    .map((summary) => {
      const gradeMonthData = monthData.find((month) => month.grade_position === summary.grade_position)
      if (!gradeMonthData) return null

      const total_count = summary.data.find((value) => value.category === 'total_count')?.count ?? 0
      const student_count = summary.data.find((value) => value.category === 'student_count')?.count ?? 0
      const fullMonthData = fillMonths(gradeMonthData.months, period, {
        count: 0,
      })

      return {
        grade_position: summary.grade_position,
        summary: {
          total_count,
          student_count,
        },
        months: fullMonthData,
      }
    })
    .filter((data) => data !== null)
}
